import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatDateFR, formatDateLongFR, getWeekdayFR, getNowParis, getDateISOParis, preserveScrollPosition } from "../lib/utils";
import ConfirmModal from "./ConfirmModal";
import { toast } from 'sonner';
import { DashboardSkeleton } from "./ui/Skeleton";
import { useAppConfig } from "../context/AppConfigContext";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { appConfig } = useAppConfig();
  const { canAccess } = useAuth();
  const companyName = appConfig?.company_name || null;
  const [dailySummary, setDailySummary] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [recentMissions, setRecentMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => getDefaultDate());
  const [dayRecap, setDayRecap] = useState([]);
  const [switchHour, setSwitchHour] = useState(20); // Heure de basculement (20h par défaut)
  const [showSettings, setShowSettings] = useState(false);
  // Sur mobile, afficher par défaut "Journée employé", sur desktop "list"
  const [activeTab, setActiveTab] = useState(() => {
    const isMobile = window.innerWidth < 768;
    return isMobile ? 'employee' : 'list';
  });
  const [employees, setEmployees] = useState([]);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [generatedText, setGeneratedText] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [showMissingParuresModal, setShowMissingParuresModal] = useState(false);
  
  // États pour les employés du lendemain
  const [tomorrowEmployees, setTomorrowEmployees] = useState([]);
  const [showTomorrowSection, setShowTomorrowSection] = useState(true);
  
  // États pour la gestion des saisies locales (sans appel API immédiat)
  const [editingParures, setEditingParures] = useState({}); // {missionId: value}
  const [editingNotes, setEditingNotes] = useState({}); // {missionId: value}
  const [editingBonus, setEditingBonus] = useState({}); // {missionId: value} - Bonus intervenant
  
  // États pour le modal de confirmation
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {}
  });
  const [editingMissionNotes, setEditingMissionNotes] = useState({}); // {missionId: value} - Notes générales de mission
  const [savingStates, setSavingStates] = useState({}); // {missionId: 'saving' | 'success' | 'error'}
  
  // Logements prêts - liens WhatsApp
  const [showLogementsPretsSection, setShowLogementsPretsSection] = useState(true);
  
  // Modal édition parures depuis onglet Informations
  const [showEditParuresModal, setShowEditParuresModal] = useState(false);
  const [editingMissionData, setEditingMissionData] = useState(null);
  const [editParuresValue, setEditParuresValue] = useState('');

  // Fonction pour obtenir la date par défaut (aujourd'hui ou demain selon l'heure)
  function getDefaultDate() {
    // Utiliser le fuseau horaire Europe/Paris
    const now = getNowParis();
    const currentHour = now.getHours();
    const switchHour = parseInt(localStorage.getItem('dashboardSwitchHour') || '20');
    
    console.log('getDefaultDate() - now:', now.toString());
    console.log('getDefaultDate() - currentHour:', currentHour);
    console.log('getDefaultDate() - switchHour:', switchHour);
    
    if (currentHour >= switchHour) {
      // Afficher le lendemain
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowISO = getDateISOParis(tomorrow);
      console.log('getDefaultDate() - returning tomorrow:', tomorrowISO);
      return tomorrowISO;
    }
    const todayISO = getDateISOParis(now);
    console.log('getDefaultDate() - returning today:', todayISO);
    return todayISO;
  }

  // Charger l'heure de basculement depuis localStorage
  useEffect(() => {
    const savedHour = localStorage.getItem('dashboardSwitchHour');
    if (savedHour) {
      setSwitchHour(parseInt(savedHour));
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    loadDayRecap();
    loadEmployees();
    loadTomorrowEmployees();
  }, [selectedDate]);

  const loadEmployees = async () => {
    if (!canAccess("employees")) {
      setEmployees([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data.filter(e => e.active));
    } catch (error) {
      console.error("Erreur chargement employées:", error);
    }
  };
  
  // Charger les employés qui travaillent demain
  const loadTomorrowEmployees = async () => {
    if (!canAccess("employees") || !canAccess("planning")) {
      setTomorrowEmployees([]);
      return;
    }
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/missions`);
      const tomorrowMissions = response.data.filter(m => {
        const mission = m.mission || m;
        return mission.date_mission === tomorrowStr && mission.employee_id;
      });
      
      // Grouper par employé
      const employeeMap = {};
      for (const m of tomorrowMissions) {
        const mission = m.mission || m;
        const empId = mission.employee_id;
        if (!employeeMap[empId]) {
          employeeMap[empId] = {
            missions: [],
            employee: null
          };
        }
        employeeMap[empId].missions.push(m);
      }
      
      // Charger les infos des employés
      const empResponse = await axios.get(`${API}/employees`);
      const allEmployees = empResponse.data;
      
      const result = [];
      for (const empId of Object.keys(employeeMap)) {
        const emp = allEmployees.find(e => e.id === empId);
        if (emp) {
          employeeMap[empId].employee = emp;
          result.push(employeeMap[empId]);
        }
      }
      
      setTomorrowEmployees(result);
    } catch (error) {
      console.error("Erreur chargement employés demain:", error);
    }
  };
  
  // Générer le message WhatsApp pour un employé
  const generateWhatsAppMessage = (empData) => {
    const now = new Date();
    const currentHour = now.getHours();
    const jours = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    
    // Après 20h, on parle de "demain", sinon on parle d'"aujourd'hui"
    let targetDate = new Date();
    let rappelText;
    
    if (currentHour >= 20) {
      // Entre 20h et minuit : rappel pour demain
      targetDate.setDate(targetDate.getDate() + 1);
      rappelText = 'demain';
    } else {
      // Entre minuit et 20h : rappel pour aujourd'hui
      rappelText = "aujourd'hui";
    }
    
    const dateStr = `${jours[targetDate.getDay()]} ${targetDate.getDate()}/${targetDate.getMonth() + 1}`;
    
    const emp = empData.employee;
    const missions = empData.missions;
    
    let message = `Bonjour ${emp.prenom} 👋\n\n`;
    message += `📅 Rappel pour ${rappelText} (${dateStr}) :\n\n`;
    
    missions.forEach((m, idx) => {
      const mission = m.mission || m;
      const housing = m.housing;
      if (housing) {
        message += `${idx + 1}. 🏠 ${housing.pseudo}\n`;
        message += `   📍 ${housing.adresse || ''} ${housing.ville || ''}\n`;
        if (housing.creneau_debut) {
          message += `   🕐 ${housing.creneau_debut}${housing.creneau_fin ? ' - ' + housing.creneau_fin : ''}\n`;
        }
      } else {
        message += `${idx + 1}. 📅 DIVERS: ${mission.notes || ''}\n`;
      }
      message += '\n';
    });
    
    message += `📱 Lien planning : ${window.location.origin}/planning-employe/${emp.id}\n\n`;
    message += `Bonne journée ! 🌟`;
    if (companyName) {
      message += `\n- ${companyName}`;
    }
    
    return message;
  };
  
  // Ouvrir WhatsApp avec le message
  const openWhatsApp = (empData) => {
    const emp = empData.employee;
    const message = generateWhatsAppMessage(empData);
    const phone = emp.telephone?.replace(/[^0-9]/g, '');
    
    // Formater le numéro pour WhatsApp (ajouter 33 si commence par 0)
    let whatsappPhone = phone;
    if (phone?.startsWith('0')) {
      whatsappPhone = '33' + phone.substring(1);
    }
    
    const url = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleAssignEmployee = async (missionId, employeeId) => {
    try {
      // Vérifier si le logement a des intervenants exclusifs
      const missionToAssign = dayRecap.flatMap(group => group.missions).find(m => m.mission.id === missionId);
      
      if (missionToAssign && missionToAssign.housing && missionToAssign.housing.intervenants_exclusifs && missionToAssign.housing.intervenants_exclusifs.length > 0) {
        const employee = employees.find(e => e.id === employeeId);
        if (employee) {
          const employeeName = `${employee.prenom} ${employee.nom}`.trim();
          const isAuthorized = missionToAssign.housing.intervenants_exclusifs.some(intervenant => 
            employeeName.toLowerCase().includes(intervenant.toLowerCase()) || 
            intervenant.toLowerCase().includes(employee.prenom.toLowerCase())
          );
          
          if (!isAuthorized) {
            // Afficher le modal de confirmation au lieu de window.confirm
            setConfirmModal({
              isOpen: true,
              title: "Avertissement - Intervenants Exclusifs",
              message: `Ce logement est exclusif aux intervenants suivants :\n\n${missionToAssign.housing.intervenants_exclusifs.join(", ")}\n\nVous essayez d'assigner ${employeeName}.\n\nVoulez-vous continuer quand même ?`,
              onConfirm: async () => {
                await performAssignEmployee(missionId, employeeId);
              }
            });
            return; // Attendre la confirmation de l'utilisateur
          }
        }
      }
      
      // Si pas d'intervenants exclusifs ou autorisé, effectuer l'assignation directement
      await performAssignEmployee(missionId, employeeId);
      
    } catch (error) {
      console.error("Erreur assignation:", error);
      toast.error("Erreur lors de l'assignation");
    }
  };
  
  // Fonction pour effectuer réellement l'assignation
  const performAssignEmployee = async (missionId, employeeId) => {
    try {
      await axios.put(`${API}/missions/${missionId}`, {
        employee_id: employeeId || null
      });
      await loadDayRecap();
      toast.success("Employée assignée avec succès");
    } catch (error) {
      console.error("Erreur assignation:", error);
      toast.error("Erreur lors de l'assignation");
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Récupérer le résumé quotidien
      const dailyResponse = await axios.get(`${API}/dashboard/daily/${selectedDate}`);
      setDailySummary(dailyResponse.data);

      // Récupérer le résumé hebdomadaire (lundi de la semaine)
      const date = new Date(selectedDate);
      const monday = new Date(date);
      monday.setDate(date.getDate() - date.getDay() + 1);
      const mondayStr = monday.toISOString().split('T')[0];
      
      const weeklyResponse = await axios.get(`${API}/dashboard/weekly/${mondayStr}`);
      setWeeklySummary(weeklyResponse.data);

      // Récupérer les missions d'aujourd'hui pour l'onglet "Informations des employés"
      const missionsResponse = await axios.get(`${API}/missions?date=${selectedDate}`);
      setRecentMissions(missionsResponse.data);
      
    } catch (error) {
      console.error("Erreur lors du chargement du dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadDayRecap = async () => {
    try {
      // Récupérer les missions du jour sélectionné avec le paramètre date
      // Utiliser from_yesterday=false pour inclure les dates passées
      const missionsResponse = await axios.get(`${API}/missions?date=${selectedDate}&from_yesterday=false`);
      
      console.log('Selected date:', selectedDate);
      console.log('Total missions for date:', missionsResponse.data.length);
      
      // Les missions sont déjà filtrées par l'API, pas besoin de re-filtrer
      const dayMissions = missionsResponse.data;

      // Grouper par employée
      const groupedByEmployee = {};
      
      dayMissions.forEach((missionData) => {
        const employeeId = missionData.employee?.id || 'unassigned';
        const employeeName = missionData.employee 
          ? `${missionData.employee.prenom} ${missionData.employee.nom}`
          : 'Non assignée';
        
        if (!groupedByEmployee[employeeId]) {
          groupedByEmployee[employeeId] = {
            employee: missionData.employee,
            employeeName: employeeName,
            missions: []
          };
        }
        
        groupedByEmployee[employeeId].missions.push(missionData);
      });

      // Trier les missions de chaque employée par heure de début
      Object.values(groupedByEmployee).forEach(group => {
        group.missions.sort((a, b) => {
          const timeA = a.housing?.creneau_debut || a.mission.heure_debut || '00:00';
          const timeB = b.housing?.creneau_debut || b.mission.heure_debut || '00:00';
          return timeA.localeCompare(timeB);
        });
      });

      setDayRecap(Object.values(groupedByEmployee));
    } catch (error) {
      console.error("Erreur lors du chargement du récapitulatif:", error);
    }
  };

  const handleSwitchHourChange = (newHour) => {
    setSwitchHour(newHour);
    localStorage.setItem('dashboardSwitchHour', newHour.toString());
  };

  const goToToday = () => {
    setSelectedDate(getDateISOParis());
  };

  const goToYesterday = () => {
    const yesterday = new Date(getNowParis());
    yesterday.setDate(yesterday.getDate() - 1);
    setSelectedDate(getDateISOParis(yesterday));
  };

  const goToTomorrow = () => {
    const tomorrow = new Date(getNowParis());
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(getDateISOParis(tomorrow));
  };

  // Mettre à jour le nombre de parures sales
  const handleParuresSalesChange = async (missionId, value) => {
    // Sauvegarder uniquement localement (pas d'appel API)
    setEditingParures(prev => ({
      ...prev,
      [missionId]: value
    }));
  };

  const saveParuresSales = async (missionId) => {
    const value = editingParures[missionId];
    if (value === undefined) return;
    
    const numValue = value === '' ? null : parseInt(value);
    
    setSavingStates(prev => ({ ...prev, [missionId]: 'saving' }));
    
    try {
      await axios.patch(`${API}/missions/${missionId}/parures-sales`, {
        parures_sales: numValue
      });
      
      // Marquer comme validé de manière permanente
      setSavingStates(prev => ({ ...prev, [missionId]: 'validated' }));
      
      // Recharger le récapitulatif
      await loadDayRecap();
    } catch (error) {
      console.error("Erreur lors de la mise à jour des parures sales:", error);
      setSavingStates(prev => ({ ...prev, [missionId]: 'error' }));
      
      setTimeout(() => {
        setSavingStates(prev => {
          const newState = { ...prev };
          delete newState[missionId];
          return newState;
        });
      }, 3000);
    }
  };

  // Mettre à jour les notes sur les parures sales
  const handleParuresNotesChange = async (missionId, value) => {
    // Sauvegarder uniquement localement (pas d'appel API)
    setEditingNotes(prev => ({
      ...prev,
      [missionId]: value
    }));
  };

  const saveParuresNotes = async (missionId) => {
    const value = editingNotes[missionId];
    if (value === undefined) return;
    
    setSavingStates(prev => ({ ...prev, [`notes_${missionId}`]: 'saving' }));
    
    try {
      await axios.patch(`${API}/missions/${missionId}/parures-notes`, {
        parures_notes: value
      });
      
      // Marquer comme validé de manière permanente
      setSavingStates(prev => ({ ...prev, [`notes_${missionId}`]: 'validated' }));
      
      await loadDayRecap();
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notes:", error);
      setSavingStates(prev => ({ ...prev, [`notes_${missionId}`]: 'error' }));
      
      setTimeout(() => {
        setSavingStates(prev => {
          const newState = { ...prev };
          delete newState[`notes_${missionId}`];
          return newState;
        });
      }, 3000);
    }
  };

  // Sauvegarder le bonus intervenant
  const saveBonusIntervenant = async (missionId) => {
    const value = editingBonus[missionId];
    if (value === undefined) return;
    
    setSavingStates(prev => ({ ...prev, [`bonus_${missionId}`]: 'saving' }));
    
    try {
      await axios.patch(`${API}/missions/${missionId}/bonus-intervenant?bonus=${parseFloat(value) || 0}`);
      
      setSavingStates(prev => ({ ...prev, [`bonus_${missionId}`]: 'validated' }));
      
      await loadDayRecap();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du bonus:", error);
      setSavingStates(prev => ({ ...prev, [`bonus_${missionId}`]: 'error' }));
      
      setTimeout(() => {
        setSavingStates(prev => {
          const newState = { ...prev };
          delete newState[`bonus_${missionId}`];
          return newState;
        });
      }, 3000);
    }
  };


  // Mettre à jour les notes générales de mission
  const handleMissionNotesChange = async (missionId, value) => {
    // Sauvegarder uniquement localement (pas d'appel API)
    setEditingMissionNotes(prev => ({
      ...prev,
      [missionId]: value
    }));
  };

  const saveMissionNotes = async (missionId) => {
    const value = editingMissionNotes[missionId];
    if (value === undefined) return;
    
    setSavingStates(prev => ({ ...prev, [`mission_notes_${missionId}`]: 'saving' }));
    
    try {
      // Mettre à jour les notes de la mission
      await axios.put(`${API}/missions/${missionId}`, {
        notes: value
      });
      
      // Détecter si les notes contiennent des produits (pré-commande)
      const hasProducts = /(\d+\s*[xX×]\s*\w+|apporter|acheter|commander|livrer|produit)/i.test(value);
      
      if (hasProducts && value.trim().length > 0) {
        // Créer automatiquement une pré-commande
        const mission = dayRecap
          .flatMap(g => g.missions || [])
          .find(m => m.mission.id === missionId);
        if (mission && mission.housing) {
            try {
              const response = await axios.post(`${API}/precommandes/create-from-text`, null, {
                params: {
                  client_id: mission.housing.client_id,
                  housing_id: mission.housing.id,
                  remarque: value
                }
              });
              
              if (response.data.count > 0) {
                toast.success(`${response.data.count} pré-commande(s) créée(s) avec succès !\n\nCes produits seront automatiquement ajoutés aux prochaines missions de ce logement.`);
              } else {
                toast.warning('Aucun produit détecté. Utilisez un format comme "3x lessive" ou "apporter gel WC"');
              }
            } catch (preorderError) {
              console.error('Erreur création pré-commande:', preorderError);
              toast.warning("Notes sauvegardées mais erreur lors de la création de la pré-commande");
            }
        }
      }
      
      // Marquer comme validé de manière permanente
      setSavingStates(prev => ({ ...prev, [`mission_notes_${missionId}`]: 'validated' }));
      
      await loadDayRecap();
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notes mission:", error);
      setSavingStates(prev => ({ ...prev, [`mission_notes_${missionId}`]: 'error' }));
      
      setTimeout(() => {
        setSavingStates(prev => {
          const newState = { ...prev };
          delete newState[`mission_notes_${missionId}`];
          return newState;
        });
      }, 3000);
    }
  };

  // Mettre à jour le statut "Logement prêt"
  const handleLogementPretChange = async (missionId, checked) => {
    try {
      await axios.patch(`${API}/missions/${missionId}/logement-status`, {
        logement_pret: checked
      });
      
      // Recharger immédiatement le récapitulatif pour voir le changement
      await loadDayRecap();
    } catch (error) {
      console.error("Erreur lors de la mise à jour logement prêt:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };
  
  const handleUpdateParuresFromModal = async () => {
    if (!editingMissionData) return;
    
    const paruresValue = editParuresValue === '' ? null : parseInt(editParuresValue);
    
    try {
      await axios.patch(`${API}/missions/${editingMissionData.mission.id}/parures-sales`, null, {
        params: { parures_sales: paruresValue }
      });
      
      toast.success("Nombre de parures mis à jour avec succès");
      setShowEditParuresModal(false);
      setEditingMissionData(null);
      setEditParuresValue('');
      await loadDayRecap();
    } catch (error) {
      console.error("Erreur mise à jour parures:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Vérifier si des parures ne sont pas renseignées en fin de journée
  const checkMissingParures = () => {
    let missingCount = 0;
    dayRecap.forEach(group => {
      group.missions.forEach(missionData => {
        const isTerminee = missionData.mission.status === 'effectue' || missionData.mission.status === 'termine';
        const alerteDesactivee = missionData.housing?.desactiver_alerte_parures === true;
        const pasDeParures = missionData.housing?.gestion_parures === false;
        const estLieuPro = missionData.client?.type_client === 'professionnel';
        if (isTerminee && missionData.housing && missionData.mission.parures_sales === null && !alerteDesactivee && !pasDeParures && !estLieuPro) {
          missingCount++;
        }
      });
    });
    return missingCount;
  };

  const missingParuresCount = checkMissingParures();

  // Générer le lien personnalisé pour un employé
  const handleGenerateEmployeeLink = async (employeeId) => {
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/generate-link?mission_date=${selectedDate}`
      );
      
      const link = response.data.link;
      
      // Afficher le lien dans un modal
      setGeneratedLink(link);
      setShowLinkModal(true);
    } catch (error) {
      console.error("Erreur génération lien:", error);
      toast.error("Erreur lors de la génération du lien");
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success("Lien copié !");
    } catch (error) {
      // Fallback pour iOS - sélectionner le texte
      console.error("Clipboard error:", error);
    }
  };

  // Générer le texte de la journée pour WhatsApp
  const generateWhatsAppText = () => {
    const dateFormatted = new Date(selectedDate).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'Europe/Paris'
    });
    
    // Fonction pour reformater les consommables (enlever les prix, mettre x pièces entre parenthèses)
    const formatConsommables = (text) => {
      if (!text) return '';
      return text
        .replace(/🛒 Consommable facturé : /g, '')
        .replace(/\s*\([0-9.,]+€\)/g, '')
        .replace(/(\S+)\s+(x\d+)(?!\))/g, '$1 ($2)');
    };
    
    let text = `Notre journée du ${dateFormatted} :\n\n`;
    
    if (dayRecap.length === 0) {
      text += "Aucune mission planifiée pour cette date.";
      return text;
    }
    
    dayRecap.forEach((employeeGroup) => {
      text += `${employeeGroup.employeeName} :\n`;
      
      employeeGroup.missions.forEach((missionData) => {
        if (missionData.housing) {
          // Mission normale avec logement
          const housingName = missionData.housing.pseudo || missionData.housing.adresse;
          
          // Collecter les informations supplémentaires
          let extras = [];
          
          // Notes du logement (instructions)
          if (missionData.housing.instructions) {
            extras.push(missionData.housing.instructions);
          }
          
          // Informations complémentaires (consommables) - sans prix
          if (missionData.mission.informations_complementaires) {
            const consommables = formatConsommables(missionData.mission.informations_complementaires);
            if (consommables) {
              extras.push(`🛒 ${consommables}`);
            }
          }
          
          // Notes de la mission
          if (missionData.mission.notes) {
            extras.push(`📝 ${missionData.mission.notes}`);
          }
          
          // Construire la ligne
          text += `•  ${housingName}`;
          if (extras.length > 0) {
            text += ` ( ${extras.join(' | ')} )`;
          }
          text += '\n';
        } else {
          // Mission DIVERS (personnelle) - Afficher les détails au lieu de juste "Mission personnelle"
          let diversInfo = [];
          
          // Lieu personnalisé (lieu_custom)
          if (missionData.mission.lieu_custom) {
            diversInfo.push(missionData.mission.lieu_custom);
          }
          
          // Notes de la mission
          if (missionData.mission.notes) {
            diversInfo.push(missionData.mission.notes);
          }
          
          // Construire la ligne avec l'icône
          if (diversInfo.length > 0) {
            text += `•  📅 ${diversInfo.join(' - ')}\n`;
          } else {
            text += `•  📅 Rendez-vous personnel\n`;
          }
        }
      });
      
      text += `\n`;
    });
    
    text += `Bonne journée à tout le monde ❤️`;
    
    return text;
  };
  
  // Ouvrir la modale avec le texte généré
  const openWhatsAppModal = () => {
    const text = generateWhatsAppText();
    setGeneratedText(text);
    setShowWhatsAppModal(true);
  };
  
  // Copier le texte depuis la modale
  const copyFromModal = () => {
    navigator.clipboard.writeText(generatedText).then(() => {
      toast.success("Texte copié dans le presse-papier !\n\nVous pouvez maintenant le coller dans WhatsApp.");
      setShowWhatsAppModal(false);
    }).catch(err => {
      console.error('Erreur lors de la copie:', err);
      toast.error("Erreur lors de la copie du texte");
    });
  };
  
  // Ouvrir WhatsApp avec le texte pré-rempli
  const sendToWhatsApp = () => {
    const encodedText = encodeURIComponent(generatedText);
    
    // Utiliser web.whatsapp.com au lieu de wa.me pour éviter les blocages
    const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedText}`;
    
    window.open(whatsappUrl, '_blank');
    setShowWhatsAppModal(false);
  };

  const getStatusBadge = (status) => {
    const badges = {
      planifie: "badge badge-info",
      en_cours: "badge badge-warning", 
      termine: "badge badge-success",
      annule: "badge badge-danger"
    };
    const labels = {
      planifie: "Planifiée",
      en_cours: "En cours",
      termine: "Terminée", 
      annule: "Annulée"
    };
    return (
      <span className={badges[status] || "badge badge-secondary"}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4" data-testid="dashboard-loading">
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* En-tête avec sélecteur de date et boutons rapides */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">            
            <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-3">
              <div className="flex items-center space-x-2">
                <label className="text-base font-medium text-gray-700">Date :</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="form-input text-base px-4 py-2"
                />
              </div>
              
              <div className="flex space-x-3">
                <button onClick={goToYesterday} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors text-base">
                  ⏮️ Hier
                </button>
                <button onClick={goToToday} className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 font-medium rounded-lg transition-colors text-base">
                  📍 Aujourd'hui
                </button>
                <button onClick={goToTomorrow} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors text-base">
                  ⏭️ Demain
                </button>
                <button 
                  onClick={() => setShowSettings(!showSettings)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors text-base"
                  title="Configurer l'heure de basculement"
                >
                  ⚙️
                </button>
              </div>
            </div>
          </div>

          {/* Configuration de l'heure de basculement */}
          {showSettings && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">⚙️ Paramètres d'affichage</h3>
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700">
                  Basculer vers le lendemain à partir de :
                </label>
                <select
                  value={switchHour}
                  onChange={(e) => handleSwitchHourChange(parseInt(e.target.value))}
                  className="form-select w-24"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}h00
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600">
                  (À partir de {switchHour}h00, le dashboard affiche automatiquement le lendemain)
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alerte parures sales manquantes */}
      {missingParuresCount > 0 && (
        <div className="card border-2 border-red-500 animate-pulse">
          <div className="card-body bg-red-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-4xl">⚠️</span>
                <div>
                  <h3 className="text-lg font-bold text-red-900">
                    Parures sales non renseignées !
                  </h3>
                  <p className="text-red-700">
                    {missingParuresCount} logement{missingParuresCount > 1 ? 's' : ''} n'{missingParuresCount > 1 ? 'ont' : 'a'} pas de nombre de parures sales renseigné.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMissingParuresModal(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-colors"
              >
                ⚠️ {missingParuresCount} logement{missingParuresCount > 1 ? 's' : ''} incomplet{missingParuresCount > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section: Employés qui travaillent demain */}
      {tomorrowEmployees.length > 0 && (
        <div className="card border-2 border-purple-300">
          <div className="card-body bg-purple-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">📅</span>
                <div>
                  <h3 className="text-lg font-bold text-purple-900">
                    Employés qui travaillent demain ({(() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
                    })()})
                  </h3>
                  <p className="text-purple-700 text-sm">
                    {tomorrowEmployees.length} employé{tomorrowEmployees.length > 1 ? 's' : ''} • {tomorrowEmployees.reduce((sum, e) => sum + e.missions.length, 0)} missions
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden md:block bg-amber-100 text-amber-800 text-xs px-3 py-1.5 rounded-lg max-w-xs">
                  💡 Envoyez les rappels WhatsApp entre 20h et minuit
                </div>
                <button
                  onClick={() => setShowTomorrowSection(!showTomorrowSection)}
                  className="px-3 py-1 bg-purple-200 hover:bg-purple-300 text-purple-800 rounded-lg text-sm"
                >
                  {showTomorrowSection ? '▼ Masquer' : '▶ Afficher'}
                </button>
              </div>
            </div>
            
            {/* Conseil mobile */}
            <div className="md:hidden bg-amber-100 text-amber-800 text-xs px-3 py-2 rounded-lg mb-3">
              💡 Envoyez les rappels WhatsApp entre 20h et minuit pour que le message dise "demain"
            </div>
            
            {showTomorrowSection && (
              <div className="space-y-3 mt-4">
                {tomorrowEmployees.map((empData) => {
                  const emp = empData.employee;
                  const missions = empData.missions;
                  
                  return (
                    <div key={emp.id} className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{emp.prenom} {emp.nom || ''}</span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                              {missions.length} mission{missions.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {missions.slice(0, 3).map((m, idx) => {
                              const housing = m.housing;
                              return (
                                <span key={idx} className="mr-2">
                                  {housing ? `🏠 ${housing.pseudo}` : '📅 DIVERS'}
                                  {idx < Math.min(missions.length - 1, 2) && ' •'}
                                </span>
                              );
                            })}
                            {missions.length > 3 && <span className="text-gray-400">+{missions.length - 3} autres</span>}
                          </div>
                          {emp.telephone && (
                            <div className="text-xs text-gray-500 mt-1">
                              📞 {emp.telephone}
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => openWhatsApp(empData)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors shadow-sm"
                          title={`Envoyer un rappel WhatsApp à ${emp.prenom}`}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          <span>Rappel WhatsApp</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Bouton pour envoyer à tous */}
                {tomorrowEmployees.length > 1 && (
                  <div className="text-center pt-2">
                    <button
                      onClick={() => {
                        tomorrowEmployees.forEach((empData, idx) => {
                          setTimeout(() => openWhatsApp(empData), idx * 1000);
                        });
                      }}
                      className="text-sm text-purple-600 hover:text-purple-800 underline"
                    >
                      📨 Envoyer le rappel à tous les employés (un par un)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tableau récapitulatif du jour - avec onglets */}
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                📋 Notre journée du {formatDateLongFR(new Date(selectedDate))}
              </h2>
            </div>
            <div className="text-sm text-gray-500">
              Date sélectionnée: {selectedDate} | Missions trouvées: {dayRecap.length > 0 ? dayRecap.reduce((sum, g) => sum + g.missions.length, 0) : 0}
            </div>
          </div>
          {dayRecap.length === 0 && (
            <p className="text-gray-600 text-sm mt-1">Aucune mission planifiée pour cette date</p>
          )}
        </div>

        {/* Onglets */}
        {dayRecap.length > 0 && (
          <div>
            <div className="border-b border-gray-200">
              <nav className="flex justify-between items-center px-6" aria-label="Tabs">
                <div className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('list')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'list'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📄 Liste missions
                  </button>
                  <button
                    onClick={() => setActiveTab('employee')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'employee'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    👥 Journée employé
                  </button>
                  <button
                    onClick={() => setActiveTab('ready')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'ready'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    🏠 LOGEMENTS PRÊTS
                  </button>
                  <button
                    onClick={() => setActiveTab('infos')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'infos'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📝 INFORMATIONS EMPLOYÉS
                  </button>
                </div>
              </nav>
            </div>

            {/* Contenu onglet Liste missions (style Sous-traitance) */}
            {activeTab === 'list' && (
              <div className="card-body p-0">
                {dayRecap.map((employeeGroup) => (
                  <div key={employeeGroup.employeeId || 'non_assigne'}>
                    {/* En-tête employé */}
                    <div className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 border-t ${
                      employeeGroup.employeeName?.toLowerCase().includes('m&m') || employeeGroup.employeeName?.toLowerCase().includes('mag')
                        ? 'bg-red-50 text-red-700 border-red-100'
                        : !employeeGroup.employeeId
                          ? 'bg-gray-50 text-gray-500 border-gray-100'
                          : 'bg-purple-50 text-purple-700 border-purple-100'
                    }`}>
                      <span>{employeeGroup.employeeName || 'Non assigné'}</span>
                      <span className="ml-auto text-[10px] opacity-70 bg-white/50 px-1.5 py-0.5 rounded-full">
                        {employeeGroup.missions.filter(m => m.mission.status !== 'annule').length} mission{employeeGroup.missions.filter(m => m.mission.status !== 'annule').length > 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Missions */}
                    <div className="divide-y divide-gray-100">
                      {employeeGroup.missions.map((missionData) => {
                        const isEffectue = missionData.mission.status === 'effectue' || missionData.mission.status === 'termine';
                        const isDivers = !missionData.housing;

                        return (
                          <div
                            key={missionData.mission.id}
                            className={`px-2 py-2 flex items-center gap-2 hover:bg-gray-50`}
                            data-testid={`dashboard-mission-${missionData.mission.id}`}
                          >
                            {/* Pastille statut */}
                            {!isDivers && (
                              isEffectue ? (
                                <span className="text-lg" title="Mission effectuée">✅</span>
                              ) : (
                                <span className="w-3 h-3 rounded-full bg-red-500 shadow-sm flex-shrink-0" title="À faire"></span>
                              )
                            )}

                            {/* Nom logement + notes */}
                            {isDivers ? (
                              <span className="text-sm font-medium truncate flex-1 text-red-600">
                                🔴 {missionData.mission.notes || 'DIVERS'}
                              </span>
                            ) : (
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-800 truncate block">
                                  {missionData.housing?.pseudo || missionData.housing?.adresse || 'Logement'}
                                </span>
                                {missionData.mission.notes && (
                                  <span className="text-[11px] text-gray-500 truncate block">
                                    {missionData.mission.notes}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {/* Créneau */}
                              {!isDivers && (missionData.housing?.creneau_debut || missionData.mission.heure_debut) && (
                                <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap">
                                  {missionData.housing?.creneau_debut || missionData.mission.heure_debut || ''}{(missionData.housing?.creneau_debut || missionData.mission.heure_debut) && (missionData.housing?.creneau_fin || missionData.mission.heure_fin) ? '-' : ''}{missionData.housing?.creneau_fin || missionData.mission.heure_fin || ''}
                                </span>
                              )}

                              {/* Sélecteur employé */}
                              <select
                                value={missionData.employee?.id || missionData.mission.employee_id || ''}
                                onChange={(e) => handleAssignEmployee(missionData.mission.id, e.target.value)}
                                className="text-xs px-1.5 py-1.5 rounded border cursor-pointer bg-gray-50"
                              >
                                <option value="">—</option>
                                {employees.map(emp => (
                                  <option key={emp.id} value={emp.id}>
                                    {emp.prenom} {emp.nom || ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Contenu onglet Journée employé */}
            {activeTab === 'employee' && (
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dayRecap.map((employeeGroup) => (
                    <div
                      key={employeeGroup.employee?.id || 'unassigned'}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {/* En-tête employée */}
                      <div className={`px-4 py-3 ${
                        employeeGroup.employee 
                          ? 'bg-gradient-to-r from-blue-50 to-blue-100' 
                          : 'bg-gradient-to-r from-gray-50 to-gray-100'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                              employeeGroup.employee ? 'bg-blue-500' : 'bg-gray-400'
                            }`}>
                              {employeeGroup.employeeName.charAt(0)}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900">
                                {employeeGroup.employeeName}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {employeeGroup.missions.length} mission{employeeGroup.missions.length > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          {/* Bouton générer lien */}
                          {employeeGroup.employee && (
                            <button
                              onClick={() => handleGenerateEmployeeLink(employeeGroup.employee.id)}
                              className="btn-secondary text-sm whitespace-nowrap"
                              title="Générer le lien personnalisé pour cet employé"
                            >
                              🔗 Lien
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Liste des logements */}
                      <div className="px-4 py-3">
                        <ul className="space-y-3">
                          {employeeGroup.missions.map((missionData, idx) => {
                            return (
                            <li key={missionData.mission.id} className="flex items-start">
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold mr-2 flex-shrink-0 mt-0.5">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                {missionData.housing ? (
                                  <div>
                                    <div className="text-gray-900 font-medium">
                                      {missionData.housing.pseudo || missionData.housing.adresse}
                                    </div>
                                    {/* Notes du logement */}
                                    {missionData.housing.instructions && (
                                      <div className="text-gray-600 text-sm mt-0.5">
                                        ({missionData.housing.instructions})
                                      </div>
                                    )}
                                    
                                    {/* Lien vers la fiche ménage Google Docs */}
                                    {missionData.housing.lien_fiche_menage && (
                                      <div className="mt-1">
                                        <a
                                          href={missionData.housing.lien_fiche_menage}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center text-xs text-green-600 hover:text-green-800 underline"
                                        >
                                          📄 Fiche ménage
                                        </a>
                                      </div>
                                    )}
                                    
                                    {/* Notes de la mission - ÉDITABLE */}
                                    <div className="mt-2 space-y-2">
                                      <label className="text-xs text-gray-600 block">📝 Notes mission :</label>
                                      <div className="space-y-1">
                                        <textarea
                                          placeholder="Ajouter une note pour cette mission..."
                                          value={editingMissionNotes[missionData.mission.id] !== undefined 
                                            ? editingMissionNotes[missionData.mission.id] 
                                            : (missionData.mission.notes || '')}
                                          onChange={(e) => handleMissionNotesChange(missionData.mission.id, e.target.value)}
                                          className="form-input text-sm w-full px-2 py-1"
                                          rows="2"
                                        />
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => preserveScrollPosition(() => saveMissionNotes(missionData.mission.id))}
                                            disabled={savingStates[`mission_notes_${missionData.mission.id}`] === 'saving' || savingStates[`mission_notes_${missionData.mission.id}`] === 'validated'}
                                            className={`text-xs px-3 py-1 disabled:opacity-50 ${
                                              savingStates[`mission_notes_${missionData.mission.id}`] === 'validated' 
                                                ? 'bg-green-600 text-white hover:bg-green-700' 
                                                : 'btn-primary'
                                            }`}
                                          >
                                            {savingStates[`mission_notes_${missionData.mission.id}`] === 'saving' && '⏳ Valider'}
                                            {savingStates[`mission_notes_${missionData.mission.id}`] === 'validated' && '✅ Validé'}
                                            {!savingStates[`mission_notes_${missionData.mission.id}`] && '💾 Valider'}
                                          </button>
                                          {savingStates[`mission_notes_${missionData.mission.id}`] === 'error' && (
                                            <span className="text-red-600 text-xs font-medium">❌ Erreur</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Section parures sales - SEULEMENT le nombre si NON incluses */}
                                    {!missionData.housing?.parures_incluses_tarif_menage && (
                                      <div className="mt-2 space-y-2">
                                        <label className="text-xs text-gray-600 block">🛏️ Parures sales :</label>
                                        <div className="flex items-center space-x-2">
                                          <input
                                            type="number"
                                            min="0"
                                            placeholder="0"
                                            value={editingParures[missionData.mission.id] !== undefined 
                                              ? editingParures[missionData.mission.id] 
                                              : (missionData.mission.parures_sales || '')}
                                            onChange={(e) => handleParuresSalesChange(missionData.mission.id, e.target.value)}
                                            className="form-input text-sm w-20 px-2 py-1"
                                          />
                                          <button
                                            onClick={() => preserveScrollPosition(() => saveParuresSales(missionData.mission.id))}
                                            disabled={savingStates[missionData.mission.id] === 'saving' || savingStates[missionData.mission.id] === 'validated'}
                                            className={`text-xs px-3 py-1 disabled:opacity-50 ${
                                              savingStates[missionData.mission.id] === 'validated' 
                                                ? 'bg-green-600 text-white hover:bg-green-700' 
                                                : 'btn-primary'
                                            }`}
                                          >
                                            {savingStates[missionData.mission.id] === 'saving' && '⏳ Valider'}
                                            {savingStates[missionData.mission.id] === 'validated' && '✅ Validé'}
                                            {!savingStates[missionData.mission.id] && '💾 Valider'}
                                          </button>
                                          {savingStates[missionData.mission.id] === 'error' && (
                                            <span className="text-red-600 text-xs font-medium">❌ Erreur</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Note vigilance parures - TOUJOURS visible */}
                                    <div className="mt-2 space-y-2">
                                      <label className="text-xs text-gray-600 block">💡 Note vigilance parures :</label>
                                      <div className="space-y-1">
                                        <input
                                          type="text"
                                          placeholder="Ex: Vigilance nettoyage nécessaire..."
                                          value={editingNotes[missionData.mission.id] !== undefined 
                                            ? editingNotes[missionData.mission.id] 
                                            : (missionData.mission.parures_notes || '')}
                                          onChange={(e) => handleParuresNotesChange(missionData.mission.id, e.target.value)}
                                          className="form-input text-sm w-full px-2 py-1"
                                        />
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => preserveScrollPosition(() => saveParuresNotes(missionData.mission.id))}
                                            disabled={savingStates[`notes_${missionData.mission.id}`] === 'saving' || savingStates[`notes_${missionData.mission.id}`] === 'validated'}
                                            className={`text-xs px-3 py-1 disabled:opacity-50 ${
                                              savingStates[`notes_${missionData.mission.id}`] === 'validated' 
                                                ? 'bg-green-600 text-white hover:bg-green-700' 
                                                : 'btn-primary'
                                            }`}
                                          >
                                            {savingStates[`notes_${missionData.mission.id}`] === 'saving' && '⏳ Valider'}
                                            {savingStates[`notes_${missionData.mission.id}`] === 'validated' && '✅ Validé'}
                                            {!savingStates[`notes_${missionData.mission.id}`] && '💾 Valider'}
                                          </button>
                                          {savingStates[`notes_${missionData.mission.id}`] === 'error' && (
                                            <span className="text-red-600 text-xs font-medium">❌ Erreur</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Checkbox Logement prêt */}
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={missionData.mission.logement_pret || false}
                                          onChange={(e) => handleLogementPretChange(missionData.mission.id, e.target.checked)}
                                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                                        />
                                        <span className="text-sm font-medium text-gray-900">
                                          ✅ Logement prêt
                                        </span>
                                      </label>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-yellow-50 p-3 rounded-lg border-2 border-yellow-300">
                                    <div className="flex items-start space-x-2">
                                      <span className="text-2xl">📅</span>
                                      <div className="flex-1">
                                        <div className="font-bold text-yellow-900">
                                          {missionData.mission.lieu_custom || 'Rendez-vous personnel'}
                                        </div>
                                        {missionData.mission.notes && (
                                          <div className="text-sm text-yellow-800 mt-2">
                                            {missionData.mission.notes}
                                          </div>
                                        )}
                                        <div className="text-xs text-yellow-700 mt-2 italic">
                                          ℹ️ À titre informatif uniquement - Non facturée
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contenu onglet Logements prêts */}
            {activeTab === 'ready' && (
              <div className="card-body">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🏠 Logements prêts aujourd'hui</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Liste des logements marqués comme prêts par les employés.
                </p>
                
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Logement</th>
                        <th>Employé</th>
                        <th>Horaires</th>
                        <th>Parures</th>
                        <th>Statut</th>
                        <th>Remarques</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayRecap.flatMap(group => group.missions)
                        .filter(m => m.mission.logement_pret === true)
                        .map((missionData) => {
                          const paruresSales = missionData.mission.parures_sales || 0;
                          const paruresNotes = missionData.mission.parures_notes || '';
                          const infosEmploye = missionData.mission.informations_employe || '';
                          const infosComplementaires = missionData.mission.informations_complementaires || '';
                          const hasRemarks = infosEmploye || infosComplementaires || paruresNotes;
                          
                          return (
                          <tr key={missionData.mission.id}>
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">
                                {missionData.housing?.pseudo || missionData.housing?.adresse || '📅 DIVERS'}
                              </div>
                              {missionData.housing?.adresse && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {missionData.housing.adresse}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {missionData.employee?.prenom || 'Non assigné'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">
                                {missionData.housing?.creneau_debut || missionData.mission.heure_debut || 'N/A'} - {missionData.housing?.creneau_fin || missionData.mission.heure_fin || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {paruresSales > 0 ? (
                                <div className="flex items-center">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                    🛏️ {paruresSales} récupérée{paruresSales > 1 ? 's' : ''}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">Aucune</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                ✅ Prêt
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm max-w-md">
                                {hasRemarks ? (
                                  <div className="space-y-1">
                                    {infosEmploye && (
                                      <div className="text-gray-900">
                                        <span className="font-medium">📝 </span>{infosEmploye}
                                      </div>
                                    )}
                                    {infosComplementaires && (
                                      <div className="text-blue-700">
                                        <span className="font-medium">ℹ️ </span>{infosComplementaires}
                                      </div>
                                    )}
                                    {paruresNotes && (
                                      <div className="text-purple-700">
                                        <span className="font-medium">🛏️ </span>{paruresNotes}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic">Aucune remarque</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )})}
                      {dayRecap.flatMap(group => group.missions).filter(m => m.mission.logement_pret === true).length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            Aucun logement marqué comme prêt pour le moment
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Contenu onglet Informations */}
            {activeTab === 'infos' && (
              <div className="card-body">
                <h3 className="text-lg font-bold text-gray-900 mb-4">📝 Informations des employés</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Voici les informations laissées par les employés sur les logements.
                </p>
                
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Logement</th>
                        <th>Employé</th>
                        <th>Date</th>
                        <th>Parures</th>
                        <th>Statut</th>
                        <th>Informations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentMissions
                        .filter(m => m.mission.informations_employe && m.mission.informations_employe.trim() !== '')
                        .map((missionData) => (
                          <tr key={missionData.mission.id}>
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">
                                {missionData.housing?.pseudo || missionData.housing?.adresse || '📅 DIVERS'}
                              </div>
                              {missionData.housing?.adresse && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {missionData.housing.adresse}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {missionData.employee?.prenom || 'Non assigné'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-600">
                                {formatDateFR(missionData.mission.date_mission)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {missionData.housing && !missionData.housing.parures_incluses_tarif_menage ? (
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {missionData.mission.parures_sales !== null ? missionData.mission.parures_sales : '-'}
                                  </span>
                                  <button
                                    onClick={() => {
                                      setEditingMissionData(missionData);
                                      setEditParuresValue(missionData.mission.parures_sales !== null ? missionData.mission.parures_sales.toString() : '');
                                      setShowEditParuresModal(true);
                                    }}
                                    className="btn-secondary text-xs px-2 py-1"
                                    title="Modifier"
                                  >
                                    ✏️
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">Inclus</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {missionData.mission.logement_pret ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                  Prêt
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  En cours
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-md">
                                {missionData.mission.informations_employe}
                              </div>
                            </td>
                          </tr>
                        ))}
                      {recentMissions.filter(m => m.mission.informations_employe && m.mission.informations_employe.trim() !== '').length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            Aucune information pour le moment
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Missions récentes - MASQUÉ PAR DEMANDE UTILISATEUR */}
      {/* 
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">🎯 Missions Récentes</h2>
        </div>
        <div className="card-body">
          {recentMissions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucune mission trouvée</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Horaire</th>
                    <th>Client</th>
                    <th>Adresse</th>
                    <th>Employée</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMissions.map((missionData) => {
                    const { mission, client, housing, employee } = missionData;
                    return (
                      <tr key={mission.id} data-testid={`mission-row-${mission.id}`}>
                        <td>
                          {formatDateFR(mission.date_mission)}
                        </td>
                        <td>
                          {housing?.creneau_debut || mission.heure_debut || 'N/A'} - {housing?.creneau_fin || mission.heure_fin || 'N/A'}
                        </td>
                        <td>
                          {client ? (
                            `${client.prenom} ${client.nom}`
                          ) : (
                            <span className="text-red-600 font-medium">DIVERS</span>
                          )}
                        </td>
                        <td className="text-truncate max-w-xs">
                          {housing ? (
                            `${housing.adresse}, ${housing.ville}`
                          ) : (
                            <span className="text-red-600 italic">{mission.notes || 'Événement personnel'}</span>
                          )}
                        </td>
                        <td>
                          {employee ? (
                            <span className="text-green-600 font-medium">
                              {employee.prenom} {employee.nom}
                            </span>
                          ) : (
                            <span className="text-red-500 font-medium">Non assignée</span>
                          )}
                        </td>
                        <td>
                          {getStatusBadge(mission.status)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      */}

      {/* Modale WhatsApp */}
      {showWhatsAppModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                💬 Exporter vers WhatsApp
              </h3>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="space-y-4">
              {/* Zone de texte affichant le message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aperçu du message :
                </label>
                <textarea
                  value={generatedText}
                  onChange={(e) => setGeneratedText(e.target.value)}
                  className="form-textarea w-full h-96 font-mono text-sm"
                  rows="15"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vous pouvez modifier le texte avant de l'envoyer
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="flex space-x-3">
                <button
                  onClick={copyFromModal}
                  className="flex-1 btn-secondary flex items-center justify-center space-x-2"
                >
                  <span>📋</span>
                  <span>Copier le texte</span>
                </button>
                <button
                  onClick={sendToWhatsApp}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2"
                >
                  <span>💬</span>
                  <span>Ouvrir WhatsApp</span>
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>💡 Astuce :</strong> Cliquez sur "Copier le texte" puis collez dans WhatsApp, 
                  ou cliquez sur "Ouvrir WhatsApp" pour ouvrir directement avec le texte pré-rempli.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour afficher le lien généré */}
      {showLinkModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                🔗 Lien personnalisé généré
              </h3>
              <button
                onClick={() => setShowLinkModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="space-y-4">
              {/* Zone de texte avec le lien */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lien à envoyer à l'employé :
                </label>
                <textarea
                  value={generatedLink}
                  readOnly
                  onClick={(e) => e.target.select()}
                  className="form-textarea w-full h-24 font-mono text-sm bg-blue-50"
                  rows="3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Touchez pour sélectionner, puis copiez (maintenir + copier)
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-col space-y-2">
                <button
                  onClick={copyLinkToClipboard}
                  className="btn-secondary flex items-center justify-center space-x-2"
                >
                  <span>📋</span>
                  <span>Copier le lien</span>
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(generatedLink)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex items-center justify-center space-x-2"
                >
                  <span>💬</span>
                  <span>Envoyer par WhatsApp</span>
                </a>
                <a
                  href={`sms:?&body=${encodeURIComponent(generatedLink)}`}
                  className="btn-secondary flex items-center justify-center space-x-2"
                >
                  <span>💬</span>
                  <span>Envoyer par SMS</span>
                </a>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>💡 Sur iPhone :</strong> Touchez le lien pour le sélectionner, puis maintenez et choisissez "Copier". 
                  Ou utilisez les boutons WhatsApp/SMS ci-dessus.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Modifier Parures depuis Informations */}
      {showEditParuresModal && editingMissionData && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">✏️ Modifier le nombre de parures sales</h3>
              <button 
                onClick={() => { setShowEditParuresModal(false); setEditingMissionData(null); }} 
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Logement :</strong> {editingMissionData.housing?.pseudo || editingMissionData.housing?.adresse}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Date :</strong> {new Date(editingMissionData.mission.date_mission).toLocaleDateString('fr-FR')}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Employé :</strong> {editingMissionData.employee?.prenom || 'Non assigné'}
                </p>
                <p className="text-sm text-gray-700 mt-2 font-medium">
                  <strong>Valeur actuelle :</strong> {editingMissionData.mission.parures_sales !== null ? `${editingMissionData.mission.parures_sales} parures` : 'Non renseigné'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau nombre de parures sales
                </label>
                <input
                  type="number"
                  min="0"
                  value={editParuresValue}
                  onChange={(e) => setEditParuresValue(e.target.value)}
                  className="form-input w-full"
                  placeholder="Nombre de parures..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Laissez vide pour supprimer la valeur
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowEditParuresModal(false); setEditingMissionData(null); }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleUpdateParuresFromModal}
                  className="btn-primary"
                >
                  ✅ Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal des logements avec parures manquantes */}
      {showMissingParuresModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                ⚠️ Logements avec parures sales non renseignées
              </h3>
              <button
                onClick={() => setShowMissingParuresModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Les logements suivants n'ont pas de nombre de parures sales renseigné. 
                Veuillez compléter cette information dans l'onglet "Journée employé".
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logement</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employée</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horaires</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dayRecap.flatMap(group => group.missions)
                      .filter(missionData => {
                        const isTerminee = missionData.mission.status === 'effectue' || missionData.mission.status === 'termine';
                        return isTerminee && missionData.housing && missionData.mission.parures_sales === null && !missionData.housing.desactiver_alerte_parures && missionData.housing.gestion_parures !== false && missionData.client?.type_client !== 'professionnel';
                      })
                      .map((missionData) => (
                        <tr key={missionData.mission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-semibold text-gray-900">
                              {missionData.housing.pseudo || missionData.housing.adresse}
                            </div>
                            <div className="text-sm text-gray-500">
                              {missionData.housing.ville}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {missionData.employee ? `${missionData.employee.prenom} ${missionData.employee.nom}` : 'Non assignée'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {missionData.housing?.creneau_debut || missionData.mission.heure_debut || 'N/A'} - {missionData.housing?.creneau_fin || missionData.mission.heure_fin || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ⚠️ Parures manquantes
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowMissingParuresModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    setShowMissingParuresModal(false);
                    setActiveTab('employee');
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Aller à "Journée employé"
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmation centré */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({...confirmModal, isOpen: false})}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Continuer quand même"
        cancelText="Annuler"
      />
    </div>
  );
};

export default Dashboard;